import { useCallback, useEffect, useRef, useState } from 'react'

// The LMS insights reads. A page asks for its reports together and is in one
// of four states: loading, ready (with the answers), unavailable (the
// organization has no LMS insights) or failed (anything else).

export const UNAVAILABLE_CODE = 'lms_insights.unavailable'
export const BAD_FILTER_CODE = 'lms_insights.bad_filter'

// An API failure, however the layer handed it on: axios's error
// (error.response.status / .data) or the response itself ({ status, data }).
const statusOf = error => (error && error.response ? error.response.status : error && error.status)
const bodyOf = error => {
  const body = error && error.response ? error.response.data : error && error.data
  return body && typeof body === 'object' ? body : {}
}

// What a failed read means for the page: the organization has no LMS
// insights (404 lms_insights.unavailable, the ordinary answer for one that
// has no LMS), or it failed. A coded refusal carries a sentence meant for
// people (a range the service will not read); anything else is only the
// page's own words.
export const failureOf = error => {
  const status = statusOf(error)
  const body = bodyOf(error)
  if (status === 404 && body.code === UNAVAILABLE_CODE) return { status: 'unavailable', error }
  const message = body.code && typeof body.message === 'string' && body.message ? body.message : null
  return { status: 'error', error, message }
}

// Whether the service refused a filter it was sent (400 lms_insights.bad_filter):
// a time zone PostgreSQL does not know is one.
export const isBadFilter = error => statusOf(error) === 400 && bodyOf(error).code === BAD_FILTER_CODE

// load() answers the page's data (or rejects). It is called again whenever key
// changes (the filters, the Refresh button) and by retry(); the answer of a
// call that was overtaken is dropped.
export const useLmsReports = (load, key) => {
  const [attempt, updateAttempt] = useState(0)
  const [state, updateState] = useState({ status: 'loading' })
  const latest = useRef(load)
  latest.current = load

  useEffect(() => {
    let live = true
    updateState({ status: 'loading' })
    let pending
    try {
      pending = Promise.resolve(latest.current())
    } catch (error) {
      pending = Promise.reject(error)
    }
    pending.then(
      data => live && updateState({ status: 'ready', data }),
      error => live && updateState(failureOf(error))
    )
    return () => { live = false }
  }, [key, attempt])

  const retry = useCallback(() => updateAttempt(count => count + 1), [])

  return { ...state, retry }
}
